import { Form, Formik, FormikProps } from 'formik';
import { FormOfAddress } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolModal } from '@synergy/components';
import { useSearchParams } from 'react-router-dom';

import { Employee } from './models/employee';
import { EmployeeFormikValues } from './models/employee-formik-values';
import { EmployeeRequest } from './models/employee-request';
import { UserManagementDetailForm } from './user-management-detail-form';
import { createEmployee, fetchEmployees, patchUser } from './actions/user-management-actions';
import { useThunkDispatch } from '../../store/use-thunk-dispatch';
import { userManagementValidationSchema } from './user-management-validation-schema';

interface UserManagementDetailModalProps {
    visible: boolean;
    user: Employee | undefined;
    onClose: () => void;
}

export const UserManagementDetailModal = (props: UserManagementDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const [searchParams] = useSearchParams();

    const initialValues: EmployeeFormikValues = {
        id: props.user?.id,
        firstName: props.user?.firstName ?? '',
        lastName: props.user?.lastName ?? '',
        positionId: props.user?.positionId ?? null,
        email: props.user?.email ?? '',
        phone: props.user?.phone ?? '',
        formOfAddress: props.user?.formOfAddress ?? FormOfAddress.Mr,
        isUser: !!props.user?.user,
    };

    const handleSubmit = async (values: EmployeeFormikValues) => {
        const newUser = getUserRequestFromValues(values);
        if (!props.user) {
            if (await dispatch(createEmployee(newUser))) {
                props.onClose();
            }
        } else {
            if (await dispatch(patchUser(newUser))) {
                props.onClose();
                dispatch(fetchEmployees(searchParams));
            }
        }
    };

    const getUserRequestFromValues = (formValues: EmployeeFormikValues): EmployeeRequest => ({
        id: props.user?.id ?? -1,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        positionId: formValues.positionId,
        email: formValues.email,
        phone: formValues.phone,
        formOfAddress: formValues.formOfAddress,
        enableLogin: formValues.isUser,
    });

    const actionChildren = (formikProps: FormikProps<EmployeeFormikValues>) => (
        <>
            <PoolButton key="1" styleType="firstLevel" variant="text" onClick={() => props.onClose()}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton key="2" styleType="firstLevel" type="submit" onClick={formikProps.submitForm}>
                <FormattedMessage id="app.common.save" />
            </PoolButton>
        </>
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={userManagementValidationSchema}
            enableReinitialize={true}
        >
            {(formikProps: FormikProps<EmployeeFormikValues>) => (
                <Form onSubmit={formikProps.handleSubmit}>
                    <PoolModal
                        handleClose={() => props.onClose()}
                        isVisible={props.visible}
                        actionChildren={actionChildren(formikProps)}
                        title={
                            <FormattedMessage
                                id={props.user ? 'app.user.management.changeContact' : 'app.user.management.addContact'}
                            />
                        }
                    >
                        <UserManagementDetailForm />
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
